import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy } from 'lucide-react';
import { notify } from '../../services/notify';

/**
 * A long ID shown truncated (mono). Hover or focus opens a popover with the
 * full value and a Copy button.
 *
 * The popover is portalled and fixed-positioned: table cells clip their
 * content, so an in-cell popover would be cut off. It stays open while the
 * pointer moves from the ID into it.
 */
export function CopyableId({ id, label = 'ID' }: { id: string; label?: string }) {
  const popId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = () => {
    clearTimeout(closeTimer.current);
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
  };
  const close = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPos(null), 150);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      notify.success(`${label} copied.`);
    } catch {
      notify.error(`Couldn't copy the ${label}.`);
    }
  };

  return (
    <>
      <span
        ref={anchorRef}
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable so keyboard users reach the full ID
        tabIndex={0}
        className="block truncate font-mono text-xs text-muted"
        onMouseEnter={open}
        onMouseLeave={close}
        onFocus={open}
        onBlur={close}
        aria-describedby={pos ? popId : undefined}
      >
        {id}
      </span>
      {pos &&
        createPortal(
          // Hover handlers only keep the popover open while the pointer crosses into it; the
          // click handler only stops propagation (the Copy button inside is the control).
          // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            id={popId}
            role="tooltip"
            onMouseEnter={open}
            onMouseLeave={close}
            // The portal escapes the DOM, not React: stop a Copy click reaching a clickable row.
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[2100] flex items-center gap-2 rounded-app border border-panel-border bg-white px-3 py-2 font-body shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            <span className="select-all font-mono text-xs text-ink">{id}</span>
            <button
              type="button"
              onClick={() => void copy()}
              aria-label={`Copy ${label}`}
              className="flex h-7 w-7 items-center justify-center rounded-app border-0 bg-transparent text-muted hover:text-orange active:scale-95"
            >
              <Copy size={14} aria-hidden="true" />
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
